import React, { useCallback, useEffect, useMemo, useState, Suspense } from 'react';
import { Button, Tabs, Tab, TabList, TabPanel, useToast, Body, Link } from '@panwds/react-ui';
import { useParams, useNavigate } from 'react-router-dom';
import { HelpInPageIcon } from '@panwds/icons';

import StatusPageContainer from './StatusPageContainer';

const IncidentsPage = React.lazy(() => import('../incidents/IncidentsPage'));
const MaintenancesPage = React.lazy(() => import('../maintenances/MaintenancesPage'));
const IncidentLookupPage = React.lazy(() => import('../incidents/IncidentLookupPage'));
const MaintenanceLookupPage = React.lazy(() => import('../maintenances/MaintenanceLookupPage'));
const AdvisoriesPage = React.lazy(() => import('../advisories/AdvisoriesPage'));
const AdvisoryLookupPage = React.lazy(() => import('../advisories/AdvisoryLookupPage'));
import HelpModal from '../subscription/HelpModal';

import { unsubscribe, getSubscriptions } from '../../services/api/subscriptions';
import { useSubscriptionFlow } from '../subscription/hooks/useSubscriptionFlow';
import { useFetchProducts } from '../../services/api/products';
import { useHealthCheck, useNotificationHealthCheck } from '../../services/api/health';

import commonStyles from '../../scss/StatusPageCommon.module.scss';
import PanLogo from '../../assets/icons/PANWLOGOWHITE.svg';

import EmailOTPModal from '../subscription/EmailOTPModal';
import ManageSubscriptionsModal from '../subscription/ManageSubscriptionsModal';
import SubscriptionMenu from '../subscription/components/SubscriptionMenu';
import { DEFAULT_SUBSCRIPTION_TYPES, hydrateSubscriptionTypes } from '../subscription/utils/subscriptionTypes';
import { hydrateCheckedProducts } from '../subscription/utils/checkedProducts';
import { getSessionToken } from '../subscription/utils/session';

const TABS = ['homeTab', 'incidentsTab', 'maintenanceTab', 'advisoriesTab'];
const DEFAULT_TAB = TABS[0];

const TAB_PARAM_MAP = {
    incidents: 'incidentsTab',
    maintenance: 'maintenanceTab',
    advisories: 'advisoriesTab',
};

const StatusPage = () => {
    const navigate = useNavigate();
    const { toast, clear } = useToast();
    const { tab: tabParam, "*": wildcard } = useParams();
    const lookupId = wildcard || null;

    const { data: products = [], productsById = {} } = useFetchProducts();

    const { data: healthData, isError: isHealthError } = useHealthCheck();
    const { data: notificationHealthData, isError: isNotificationHealthError } = useNotificationHealthCheck();

    const healthStatus = healthData?.status === 'success';
    const notificationHealthStatus = notificationHealthData?.status === 'success';

    useEffect(() => {
        if (isHealthError || (healthData && !healthStatus)) {
            if (!navigator.onLine) return;
            const controller = new AbortController();
            setTimeout(() => controller.abort(), 3000);
            fetch(import.meta.env.VITE_FAILOVER_UI_URL, { mode: 'no-cors', method: 'HEAD', signal: controller.signal })
                .then(() => window.location.replace('/nothealthy'))
                .catch(() => {});
        }
    }, [healthData, healthStatus, isHealthError]);

    useEffect(() => {
        if (isNotificationHealthError || (notificationHealthData && !notificationHealthStatus)) {
            toast('Subscription functionality is not available.', { appearance: 'error' });
            setTimeout(clear, 10000);
        } else {
            clear();
        }
    }, [notificationHealthData, notificationHealthStatus, isNotificationHealthError, toast, clear]);

    // --- Tab routing ---
    const [activeTabId, setActiveTabId] = useState(() => {
        return TAB_PARAM_MAP[tabParam] || DEFAULT_TAB;
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        setActiveTabId(TAB_PARAM_MAP[tabParam] || DEFAULT_TAB);
    }, [tabParam]);

    const handleActiveTabChange = useCallback(
        (nextTabId) => {
            window.scrollTo(0, 0);
            const tabPath = nextTabId.replace('Tab', '');
            navigate({ pathname: tabPath === 'home' ? '/' : `/${tabPath}`, search: '' });
        },
        [navigate],
    );

    // --- Unsubscribe via URL params ---
    const uriParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const emailId = uriParams.get('email');
    const productTypes = uriParams.get('productTypes');
    const maintenanceId = uriParams.get('maintenanceId');
    const incidentId = uriParams.get('incidentId');
    const advisoryId = uriParams.get('advisoryId');

    const callUnsubscribe = useCallback(
        async (payload) => {
            const data = await unsubscribe({ ...payload });
            toast(data.message, { appearance: 'success' });
        },
        [toast],
    );

    useEffect(() => {
        if (tabParam !== 'unsubscribe') return;

        try {
            const payload = {
                channelType: 'email',
                channelIdentifier: emailId,
                maintenanceId,
                incidentId,
                advisoryId,
            };
            if (productTypes?.length) {
                payload.productTypes = productTypes.split(',');
            }
            callUnsubscribe(payload);
        } catch (error) {
            console.error('Error Unsubscribing:', error, emailId);
        }
    }, [tabParam, emailId, productTypes, maintenanceId, incidentId, advisoryId, callUnsubscribe]);

    // --- Subscription flow (OTP / reCAPTCHA) ---
    const subscriptionFlow = useSubscriptionFlow({
        onSubscribeSuccess: ({ subscriptionsData: sData }) => {
            if (sData) setSubscriptionsData(sData);
        },
    });

    const {
        email,
        hasValidSession,
        isManageModalOpen,
        setIsManageModalOpen,
        isEmailModalOpen,
        setIsEmailModalOpen,
        resetSession,
    } = subscriptionFlow;

    // --- Review subscription state ---
    const [subscriptionsData, setSubscriptionsData] = useState({});
    const [checkedProducts, setCheckedProducts] = useState({});
    const [subscriptionTypes, setSubscriptionTypes] = useState(DEFAULT_SUBSCRIPTION_TYPES);
    const [expandedProducts, setExpandedProducts] = useState({});

    const toggleExpandedRegions = (productName) => {
        setExpandedProducts((prev) => ({
            ...prev,
            [productName]: !prev[productName],
        }));
    };

    // Baselines are derived from server data rather than snapshotted when the modal opens:
    // `handleSubscribeClick` sets `subscriptionsData` and opens the modal in the same tick,
    // so the modal's first render would otherwise capture the *previous* subscriber's state.
    // These same values seed the editable state below, so "pristine" is exactly "matches
    // what the server last told us".
    const baselineCheckedProducts = useMemo(
        () => hydrateCheckedProducts(subscriptionsData, productsById),
        [subscriptionsData, productsById],
    );
    const baselineSubscriptionTypes = useMemo(
        () => hydrateSubscriptionTypes(subscriptionsData),
        [subscriptionsData],
    );

    useEffect(() => {
        setSubscriptionTypes(baselineSubscriptionTypes);
    }, [baselineSubscriptionTypes]);

    useEffect(() => {
        setCheckedProducts(baselineCheckedProducts);
    }, [baselineCheckedProducts]);

    // --- Modals ---
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

    const handleSubscribeClick = useCallback(async () => {
        if (hasValidSession) {
            try {
                const subscriptions = await getSubscriptions(
                    `/${localStorage.getItem('email')}?session_token=${getSessionToken()}`,
                );

                if (!subscriptions || typeof subscriptions !== 'object') {
                    console.warn('getSubscriptions returned empty/invalid response:', subscriptions);
                    setIsManageModalOpen(true);
                } else if (subscriptions.status === 'success') {
                    setSubscriptionsData(subscriptions.data);
                    setIsManageModalOpen(true);
                } else {
                    toast(subscriptions.message, { appearance: subscriptions.status });
                    resetSession();
                }
            } catch (error) {
                if (error?.response?.status === 404) {
                    setIsManageModalOpen(true);
                } else {
                    resetSession();
                }
            }
        } else {
            resetSession();
        }
    }, [hasValidSession, toast, setIsManageModalOpen, resetSession]);

    return (
        <div className="ssmutw-text-white st-text-sm panwds-tw3" data-testid="sase-status-main-ui">
            {/* Header bar */}
            <div className={commonStyles.headerBar}>
                <div className={`ssmutw-w-full ssmutw-max-w-[1320px] ssmutw-mx-auto ssmutw-px-4 md:ssmutw-px-6 ssmutw-flex ssmutw-flex-wrap ssmutw-items-center ssmutw-text-center md:ssmutw-text-left ${commonStyles.headerInner}`}>
                    <div className={commonStyles.logo} onClick={() => (window.location = '/')}>
                        <img
                            alt="Palo Alto Networks"
                            src={PanLogo}
                            width="137"
                            height="25"
                        />
                        <span>SASE HEALTH</span>
                    </div>

                    <div className={`ssmutw-min-w-0 ${commonStyles.tabsWrapper}`}>
                        <div data-active-tab={activeTabId}>
                            <Tabs>
                                <TabList activeTabId={activeTabId} onActiveChange={handleActiveTabChange} overflow="scroll">
                                    <Tab id="homeTab" size="sm">Status</Tab>
                                    <Tab id="incidentsTab" size="sm">Incidents</Tab>
                                    <Tab id="maintenanceTab" size="sm">Maintenance</Tab>
                                    <Tab id="advisoriesTab" size="sm">Advisories</Tab>
                                </TabList>
                            </Tabs>
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div className={`ssmutw-flex ssmutw-items-center ssmutw-shrink ssmutw-min-w-0 md:ssmutw-ml-auto ${commonStyles.headerActions}`}>
                        <div>
                            <SubscriptionMenu
                                hasValidSession={hasValidSession}
                                email={localStorage.getItem('email') || ''}
                                onEdit={handleSubscribeClick}
                                onSignIn={handleSubscribeClick}
                                onSignOut={subscriptionFlow.signOut}
                            />
                        </div>
                        <div className="ssmutw-pl-2">
                            <div>
                                <Button onClick={() => setIsHelpModalOpen(true)}>
                                    Help
                                    <HelpInPageIcon size="sm" className="ssmutw-ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content: lookup page or tab panels */}
            {lookupId && tabParam === "incidents" ? (
                <Suspense fallback={null}><IncidentLookupPage incidentId={lookupId} /></Suspense>
            ) : lookupId && tabParam === "maintenance" ? (
                <Suspense fallback={null}><MaintenanceLookupPage maintenanceId={lookupId} /></Suspense>
            ) : lookupId && tabParam === "advisories" ? (
                <Suspense fallback={null}><AdvisoryLookupPage advisoryId={lookupId} /></Suspense>
            ) : (
                <>
                    <TabPanel activeTabId={activeTabId} forTabId="homeTab" addClassName={commonStyles.mapTabPanel}>
                        <StatusPageContainer onHelpClick={() => setIsHelpModalOpen(true)} />
                    </TabPanel>
                    <TabPanel activeTabId={activeTabId} forTabId="incidentsTab" addClassName={commonStyles.mapTabPanel}>
                        <Suspense fallback={null}><IncidentsPage /></Suspense>
                    </TabPanel>
                    <TabPanel activeTabId={activeTabId} forTabId="maintenanceTab" addClassName={commonStyles.mapTabPanel}>
                        <Suspense fallback={null}><MaintenancesPage /></Suspense>
                    </TabPanel>
                    <TabPanel activeTabId={activeTabId} forTabId="advisoriesTab" addClassName={commonStyles.mapTabPanel}>
                        <Suspense fallback={null}><AdvisoriesPage /></Suspense>
                    </TabPanel>
                </>
            )}

            {/* Modals */}
            {isHelpModalOpen && (
                <HelpModal isOpen closeModal={() => setIsHelpModalOpen(false)} />
            )}

            {isEmailModalOpen && (
                <EmailOTPModal subscriptionFlow={subscriptionFlow} />
            )}

            {isManageModalOpen && (
                <ManageSubscriptionsModal
                    isOpen={isManageModalOpen}
                    onClose={() => setIsManageModalOpen(false)}
                    products={products}
                    checkedProducts={checkedProducts}
                    setCheckedProducts={setCheckedProducts}
                    baselineCheckedProducts={baselineCheckedProducts}
                    subscriptionTypes={subscriptionTypes}
                    setSubscriptionTypes={setSubscriptionTypes}
                    baselineSubscriptionTypes={baselineSubscriptionTypes}
                    expandedProducts={expandedProducts}
                    toggleExpandedRegions={toggleExpandedRegions}
                    subscribedIncidents={subscriptionsData?.subscribed_incidents}
                    subscribedMaintenances={subscriptionsData?.subscribed_maintenances}
                    subscribedAdvisories={subscriptionsData?.subscribed_advisories}
                    toast={toast}
                    clear={clear}
                    onSaveSuccess={async () => {
                        try {
                            const subs = await getSubscriptions(
                                `/${localStorage.getItem('email')}?session_token=${getSessionToken()}`,
                            );
                            if (subs && typeof subs === 'object' && subs.status === 'success') {
                                setSubscriptionsData(subs.data);
                            }
                        } catch {
                            // ignore — subscriptions will refresh on next open
                        }
                    }}
                />
            )}

            {/* Footer */}
            <div className="ssmutw-mt-5 ssmutw-mb-5 ssmutw-text-white ssmutw-text-center ssmutw-w-full">
                <div className={`ssmutw-flex ssmutw-flex-wrap ssmutw-items-baseline ssmutw-text-left ${commonStyles.contentWidth}`}>
                    <Body as="span" size="sm">
                        {`Copyright © ${new Date().getFullYear()} Palo Alto Networks. All Rights Reserved.`}
                    </Body>
                    <span className="ssmutw-ml-2 ssmutw-text-white">
                        <Link size="sm" href={import.meta.env.VITE_FAILOVER_UI_URL || 'https://sase-secondary-qa.status.paloaltonetworks.com'} target="_blank">
                            Secondary Sase Health
                        </Link>
                    </span>
                </div>
            </div>
        </div>
    );
};

export default React.memo(StatusPage);
